import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import { useConfirm } from "material-ui-confirm";

import { get, post } from "../../api";
import Button from "../../components/Button";
import Checkbox from "../../components/Checkbox";
import CheckboxWithLabel from "../../components/CheckboxWithLabel";
import Frame from "../../components/Frame";
import FrameHeader from "../../components/FrameHeader";
import Icon from "../../components/Icon";
import Loading from "../../components/Loading";
import LoadingFrame from "../../components/LoadingFrame";
import Modal from "../../components/Modal";
import TextInput from "../../components/TextInput";

import styles from "./AdminPortalLicenses.module.scss";

/**
 * The server extends by whole license years of 365 days, matching the store
 * renewal paths. Preview the new expiry the same way so the number shown before
 * the extension is the number written after it.
 */
const DAYS_IN_LICENSE_YEAR = 365;
const EXTENSION_PERIODS = [1, 3, 5];

const DATE_FORMAT = "MMM D, YYYY";

function formatDate(value) {
  return value ? moment.utc(value).format(DATE_FORMAT) : "—";
}

function previewExpiry(expiry, years) {
  return moment
    .utc(expiry)
    .add(DAYS_IN_LICENSE_YEAR * years, "days")
    .format(DATE_FORMAT);
}

function deviceLabel(license) {
  return (
    license.device_name ||
    (license.serial_number ? `#${license.serial_number}` : null) ||
    `Device ${license.device_id || "—"}`
  );
}

/** Device state as one string. Status carries most of it; active is the qualifier. */
function deviceState(status, active) {
  return active ? String(status) : `${status} (inactive)`;
}

/** Assets have no status column - maintenance and active are the whole story. */
function assetState(maintenance, active) {
  if (maintenance) return "Maintenance";
  return active ? "Active" : "Inactive";
}

function pluralise(count, noun) {
  return `${count} ${noun}${count === 1 ? "" : "s"}`;
}

export default function AdminPortalLicenses() {
  const confirm = useConfirm();

  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");

  const [expanded, setExpanded] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState([]);

  const [selected, setSelected] = useState([]);
  const [years, setYears] = useState(EXTENSION_PERIODS[0]);
  const [extending, setExtending] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  /**
   * The search term travels with the per-customer request too. A row that only
   * surfaced because one of its devices matched is expanded to that device, not
   * to the customer's whole fleet, and the counts in the collapsed row were
   * counted the same way.
   */
  const loadCustomerLicenses = useCallback(
    async (customerId) => {
      setLoadingCustomers((current) => [...current, customerId]);

      const res = await get("/licenses/extendable", {
        customer_id: customerId,
        search: appliedSearch,
      });

      const detail = res && res.customers && res.customers[0];

      if (detail) {
        setCustomers((current) =>
          current.map((customer) =>
            customer.customer_id === customerId ? detail : customer
          )
        );
      }

      setLoadingCustomers((current) =>
        current.filter((id) => id !== customerId)
      );
    },
    [appliedSearch]
  );

  const loadCustomers = useCallback(async () => {
    setLoading(true);
    setError(null);

    const res = await get("/licenses/extendable", {
      search: appliedSearch,
    });

    if (res === undefined) {
      setError("Could not load licenses. Try again.");
      setLoading(false);
      return;
    }

    const list = res.customers || [];

    setCustomers(list);
    setSelected([]);

    /**
     * Searching a serial or a device id all but always lands on one customer, and
     * leaving it collapsed would mean the device you just typed is still a click
     * away. Open it and pull its devices straight away.
     */
    if (appliedSearch && list.length === 1) {
      setExpanded([list[0].customer_id]);
      loadCustomerLicenses(list[0].customer_id);
    } else {
      setExpanded([]);
    }

    setLoading(false);
  }, [appliedSearch, loadCustomerLicenses]);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  function toggleCustomer(customer) {
    const isOpen = expanded.includes(customer.customer_id);

    setExpanded((current) =>
      isOpen
        ? current.filter((id) => id !== customer.customer_id)
        : [...current, customer.customer_id]
    );

    if (!isOpen && !customer.loaded) {
      loadCustomerLicenses(customer.customer_id);
    }
  }

  function toggleLicense(licenseId) {
    setSelected((current) =>
      current.includes(licenseId)
        ? current.filter((id) => id !== licenseId)
        : [...current, licenseId]
    );
  }

  /**
   * Any license can be extended whatever state its device is in, so every row
   * is selectable. Device status is still shown on the row as context.
   */
  function selectableIdsFor(customer) {
    return (customer.licenses || []).map((license) => license.license_id);
  }

  function toggleAllForCustomer(customer) {
    const ids = selectableIdsFor(customer);
    const allSelected =
      ids.length > 0 && ids.every((id) => selected.includes(id));

    setSelected((current) =>
      allSelected
        ? current.filter((id) => !ids.includes(id))
        : [...current, ...ids.filter((id) => !current.includes(id))]
    );
  }

  function selectedCountFor(customer) {
    return (customer.licenses || []).filter((license) =>
      selected.includes(license.license_id)
    ).length;
  }

  async function extendSelected() {
    try {
      await confirm({
        title: "Extend licenses",
        description: `Extend ${pluralise(
          selected.length,
          "license"
        )} by ${years} year${
          years === 1 ? "" : "s"
        }? The customer will not be notified.`,
        dialogProps: { fullScreen: false },
        cancellationButtonProps: { color: "error", disableRipple: true },
        confirmationButtonProps: { color: "primary", disableRipple: true },
      });
    } catch (e) {
      /** Dismissed the confirmation. */
      return;
    }

    setExtending(true);
    setError(null);

    const res = await post("/licenses/extend", {
      license_ids: selected,
      years,
    });

    setExtending(false);

    if (res === undefined) {
      setError("The extension could not be completed. Try again.");
      return;
    }

    setResult(res);
    setSelected([]);

    /**
     * Expiry dates and eligibility both move as a result of the extension, so
     * the rows on screen are stale. Reload the customers that were open.
     */
    const openCustomers = customers.filter((customer) =>
      expanded.includes(customer.customer_id)
    );

    setCustomers((current) =>
      current.map((customer) =>
        expanded.includes(customer.customer_id)
          ? { ...customer, loaded: false, licenses: [] }
          : customer
      )
    );

    openCustomers.forEach((customer) =>
      loadCustomerLicenses(customer.customer_id)
    );
  }

  function renderLicense(license) {
    const isSelected = selected.includes(license.license_id);

    const rowClasses = [
      styles.licenseRow,
      isSelected ? styles.licenseRowSelected : "",
    ]
      .filter((c) => c)
      .join(" ");

    return (
      <div key={license.license_id} className={rowClasses}>
        <div className={styles.licenseCheckbox}>
          <Checkbox
            checked={isSelected}
            onChange={() => toggleLicense(license.license_id)}
          />
        </div>

        <div className={styles.licenseMain}>
          <div className={styles.licenseName}>
            {license.device_id ? (
              <Link to={`/devices/${license.device_id}`}>
                {deviceLabel(license)}
              </Link>
            ) : (
              deviceLabel(license)
            )}
            {license.serial_number && license.device_name && (
              <span className={styles.licenseSerial}>
                #{license.serial_number}
              </span>
            )}
          </div>

          <div className={styles.licenseMeta}>
            {license.asset_id ? (
              <Link to={`/assets/${license.asset_id}`}>
                {license.asset_name || `Asset ${license.asset_id}`}
              </Link>
            ) : (
              <span className={styles.muted}>No asset</span>
            )}
            <span className={styles.divider}>·</span>
            <span className={license.device_active ? "" : styles.warn}>
              Device {license.device_status}
            </span>
            {license.asset_id && license.asset_maintenance && (
              <>
                <span className={styles.divider}>·</span>
                <span className={styles.warn}>Maintenance</span>
              </>
            )}
            {!license.license_active && (
              <>
                <span className={styles.divider}>·</span>
                <span className={styles.warn}>License inactive</span>
              </>
            )}
          </div>
        </div>

        <div className={styles.licenseExpiry}>
          <div className={styles.expiryLabel}>Expires</div>
          <div className={styles.expiryValue}>
            {formatDate(license.expiry_date)}
          </div>
          {isSelected && (
            <div className={styles.expiryNew}>
              <Icon name="arrow_forward" size={12} color="#048b06" />
              {previewExpiry(license.expiry_date, years)}
            </div>
          )}
        </div>
      </div>
    );
  }

  function renderCustomer(customer) {
    const isOpen = expanded.includes(customer.customer_id);
    const isLoading = loadingCustomers.includes(customer.customer_id);
    const selectedCount = selectedCountFor(customer);
    const selectableIds = selectableIdsFor(customer);
    const allSelected =
      selectableIds.length > 0 &&
      selectableIds.every((id) => selected.includes(id));

    return (
      <div key={customer.customer_id} className={styles.customer}>
        <button
          type="button"
          className={styles.customerHeader}
          onClick={() => toggleCustomer(customer)}
          aria-expanded={isOpen}
        >
          <Icon name={isOpen ? "expand_less" : "expand_more"} size={22} />
          <span className={styles.customerName}>{customer.customer_name}</span>
          <span className={styles.customerCounts}>
            {pluralise(customer.license_count, "license")}
          </span>
          {selectedCount > 0 && (
            <span className={styles.customerSelected}>
              {selectedCount} selected
            </span>
          )}
        </button>

        {isOpen && (
          <div className={styles.customerBody}>
            {isLoading ? (
              <div className={styles.customerLoading}>
                <Loading color="gray" size={24} />
              </div>
            ) : (customer.licenses || []).length === 0 ? (
              <div className={styles.empty}>
                No licensed devices for this customer.
              </div>
            ) : (
              <>
                {selectableIds.length > 0 && (
                  <div className={styles.selectAll}>
                    <CheckboxWithLabel
                      label={`Select all ${selectableIds.length}`}
                      checked={allSelected}
                      onChange={() => toggleAllForCustomer(customer)}
                    />
                  </div>
                )}
                {customer.licenses.map(renderLicense)}
              </>
            )}
          </div>
        )}
      </div>
    );
  }

  /**
   * What the extension did to the device and its asset. Both sides are always
   * shown: an unchanged pair collapses to one muted value, which is how you can
   * see at a glance that maintenance was left alone on a device that was never
   * expired. Only a restore moves any of these.
   */
  function renderStateChanges(row) {
    const pair = (label, before, after) => (
      <div className={styles.statePair}>
        <span className={styles.stateLabel}>{label}</span>
        {before === after ? (
          <span className={styles.stateSame}>{before} (unchanged)</span>
        ) : (
          <>
            <span className={styles.stateBefore}>{before}</span>
            <Icon name="arrow_forward" size={12} color="#048b06" />
            <span className={styles.stateAfter}>{after}</span>
          </>
        )}
      </div>
    );

    return (
      <div className={styles.resultState}>
        {pair(
          "Device",
          deviceState(row.device_status_before, row.device_active_before),
          deviceState(row.device_status_after, row.device_active_after)
        )}
        {row.asset_id != null &&
          pair(
            row.asset_name ? `Asset · ${row.asset_name}` : "Asset",
            assetState(row.asset_maintenance_before, row.asset_active_before),
            assetState(row.asset_maintenance_after, row.asset_active_after)
          )}
      </div>
    );
  }

  function renderResultModal() {
    const rows = result.results || [];
    const extended = rows.filter((r) => r.extended);
    const skipped = rows.filter((r) => !r.extended);
    const restoredCount = extended.filter((r) => r.restored).length;

    return (
      <Modal
        footer={
          <Button theme="primary" onClick={() => setResult(null)}>
            Done
          </Button>
        }
      >
        <FrameHeader title="Extension complete" />

        <div className={styles.resultSummary}>
          <strong>{pluralise(result.extended_count, "license")}</strong> extended
          by {result.years} year{result.years === 1 ? "" : "s"}
          {result.skipped_count > 0 && (
            <>
              , <strong>{result.skipped_count}</strong> skipped
            </>
          )}
          .
          {restoredCount > 0 && (
            <>
              {" "}
              <strong>{pluralise(restoredCount, "device")}</strong> brought back
              from an expired license.
            </>
          )}
        </div>

        {extended.map((row) => (
          <div key={row.license_id} className={styles.resultRow}>
            <Icon name="check_circle" size={16} color="#048b06" />
            <span className={styles.resultName}>
              {row.device_name ||
                (row.serial_number ? `#${row.serial_number}` : null) ||
                `License ${row.license_id}`}
            </span>
            <span className={styles.muted}>
              {formatDate(row.previous_expiry_date)} →{" "}
              {formatDate(row.new_expiry_date)}
            </span>
            {/* The device was held down by the expired license and came back with it. */}
            {row.restored && (
              <span className={styles.restored}>Device restored</span>
            )}
            {renderStateChanges(row)}
          </div>
        ))}

        {skipped.map((row) => (
          <div key={row.license_id} className={styles.resultRow}>
            <Icon name="block" size={16} color="#ba0808" />
            <span className={styles.resultName}>
              {row.device_name ||
                (row.serial_number ? `#${row.serial_number}` : null) ||
                `License ${row.license_id}`}
            </span>
            <span className={styles.muted}>{row.reason}</span>
          </div>
        ))}
      </Modal>
    );
  }

  return (
    <>
      <FrameHeader title="Extend licenses" />

      <Frame>
        <form
          className={styles.filters}
          onSubmit={(e) => {
            e.preventDefault();
            setAppliedSearch(search.trim());
          }}
        >
          <div className={styles.searchField}>
            <TextInput
              value={search}
              icon="search"
              placeholder="Customer name, device ID or serial…"
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <Button type="submit">Search</Button>
          {appliedSearch && (
            <Button
              onClick={() => {
                setSearch("");
                setAppliedSearch("");
              }}
            >
              Clear
            </Button>
          )}
        </form>

        {appliedSearch && (
          <div className={styles.searchHint}>
            Showing customers and devices matching{" "}
            <strong>{appliedSearch}</strong>. A customer matched by name lists
            all of their devices.
          </div>
        )}
      </Frame>

      <div className={styles.actionBar}>
        <div className={styles.actionCount}>
          {selected.length === 0
            ? "No devices selected"
            : `${pluralise(selected.length, "device")} selected`}
        </div>

        <div className={styles.periods}>
          <span className={styles.periodsLabel}>Extend by</span>
          {EXTENSION_PERIODS.map((period) => (
            <button
              key={period}
              type="button"
              className={[
                styles.periodButton,
                years === period ? styles.periodButtonActive : "",
              ]
                .filter((c) => c)
                .join(" ")}
              onClick={() => setYears(period)}
            >
              {period} year{period === 1 ? "" : "s"}
            </button>
          ))}
        </div>

        <Button
          theme="primary"
          disabled={selected.length === 0 || extending}
          isLoading={extending}
          onClick={extendSelected}
        >
          Extend
        </Button>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {loading ? (
        <LoadingFrame />
      ) : customers.length === 0 ? (
        <Frame>
          <div className={styles.empty}>No customers with licenses found.</div>
        </Frame>
      ) : (
        <Frame padding={0}>{customers.map(renderCustomer)}</Frame>
      )}

      {result && renderResultModal()}
    </>
  );
}
